export const MISSING_METRIC_LABEL = "\u2014";

export const finiteMetric = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const hasDelivery = (statistics) =>
  (finiteMetric(statistics?.impressions) ?? 0) > 0 ||
  (finiteMetric(statistics?.click) ?? 0) > 0;

export const hasAnyPositiveMetric = (statistics) =>
  ["impressions", "click", "mainAction"].some(
    (key) => (finiteMetric(statistics?.[key]) ?? 0) > 0
  );

export const canShowPerformingEntity = ({ entity, statistics }) =>
  Boolean(entity?.id) && hasDelivery(statistics);

export const sumOptionalMetrics = (left, right) => {
  const leftValue = finiteMetric(left);
  const rightValue = finiteMetric(right);
  if (leftValue === null && rightValue === null) return null;
  return (leftValue ?? 0) + (rightValue ?? 0);
};

export const initialOptionalMetrics = (source) => ({
  impressions: finiteMetric(source?.impressions),
  click: finiteMetric(source?.click),
  mainAction: finiteMetric(source?.mainAction),
});

export const accumulateOptionalMetrics = ({ target, source }) => {
  ["impressions", "click", "mainAction"].forEach((key) => {
    target[key] = sumOptionalMetrics(target[key], source?.[key]);
  });
  return target;
};

export const formatTranslatedMetric = ({ t, key, value }) => {
  const numeric = finiteMetric(value);
  if (numeric === null || !key) return MISSING_METRIC_LABEL;
  return t(key, { value: numeric });
};

export const comparePerformanceStats = (leftStats = {}, rightStats = {}) => {
  const leftMain = finiteMetric(leftStats?.mainAction) ?? 0;
  const rightMain = finiteMetric(rightStats?.mainAction) ?? 0;
  if (leftMain !== rightMain) return rightMain - leftMain;

  const leftClicks = finiteMetric(leftStats?.click) ?? 0;
  const rightClicks = finiteMetric(rightStats?.click) ?? 0;
  if (leftClicks !== rightClicks) return rightClicks - leftClicks;

  const leftImpressions = finiteMetric(leftStats?.impressions) ?? 0;
  const rightImpressions = finiteMetric(rightStats?.impressions) ?? 0;
  return rightImpressions - leftImpressions;
};

export const takeTopPerformers = ({
  items = [],
  limit = 5,
  getStatistics,
  includeItem = () => true,
}) =>
  items
    .filter((item) => includeItem(item))
    .toSorted((left, right) =>
      comparePerformanceStats(getStatistics(left), getStatistics(right))
    )
    .slice(0, limit);

export const pickMostViewedText = (items = []) => {
  let best = null;

  items.forEach((current) => {
    if (!current?.text) return;
    const impressions = finiteMetric(current.impressions);
    if (impressions === null) return;
    if (!best || impressions > best.impressions) {
      best = { text: current.text, impressions };
    }
  });

  if (!best) return null;

  return {
    text: best.text,
    statistics: { impressions: best.impressions },
  };
};

const collectAdTextItems = (adTexts) => {
  const titles = [];
  const descriptions = [];

  Object.values(adTexts).forEach((adTextsStats) => {
    if (!adTextsStats || typeof adTextsStats !== "object") return;
    if (adTextsStats.titles && typeof adTextsStats.titles === "object") {
      titles.push(...Object.values(adTextsStats.titles));
    }
    if (
      adTextsStats.descriptions &&
      typeof adTextsStats.descriptions === "object"
    ) {
      descriptions.push(...Object.values(adTextsStats.descriptions));
    }
  });

  return { titles, descriptions };
};

export const getBestCopies = ({ adTexts, adId } = {}) => {
  const empty = { bestTitle: null, bestDescription: null };
  if (!adTexts || typeof adTexts !== "object") return empty;

  const scopedEntry = adId ? adTexts[adId] : null;
  const scoped =
    scopedEntry && typeof scopedEntry === "object"
      ? { [adId]: scopedEntry }
      : adTexts;
  const { titles, descriptions } = collectAdTextItems(scoped);

  return {
    bestTitle: pickMostViewedText(titles),
    bestDescription: pickMostViewedText(descriptions),
  };
};

export const youtubeThumbnailUrl = ({ youtubeVideoID }) =>
  youtubeVideoID
    ? `https://i.ytimg.com/vi/${encodeURIComponent(youtubeVideoID)}/hqdefault.jpg`
    : "";

export const youtubeWatchUrl = ({ youtubeVideoID }) =>
  youtubeVideoID
    ? `https://www.youtube.com/watch?v=${encodeURIComponent(youtubeVideoID)}`
    : "";

export const findCampaignEntityByID = ({ entities, id, platform }) => {
  if (!id || !Array.isArray(entities)) return null;

  return (
    entities.find((entity) => {
      if (!entity?.id || entity.id !== id) return false;
      if (platform && entity.platform !== platform) return false;
      return true;
    }) ?? null
  );
};

const numericRowMetric = (row, key) => finiteMetric(row?.[key]) ?? 0;

export const getInsightPerformanceMetricKey = (rows = []) => {
  if (rows.some((row) => numericRowMetric(row, "mainAction") > 0)) {
    return "mainAction";
  }
  if (rows.some((row) => numericRowMetric(row, "click") > 0)) return "click";
  return "impressions";
};

export const getInsightLeaderRow = (rows = []) => {
  const metricKey = getInsightPerformanceMetricKey(rows);

  return rows.reduce((best, row) => {
    if (!best) return row;
    const value = numericRowMetric(row, metricKey);
    const bestValue = numericRowMetric(best, metricKey);
    if (value !== bestValue) return value > bestValue ? row : best;

    const clicks = numericRowMetric(row, "click");
    const bestClicks = numericRowMetric(best, "click");
    if (clicks !== bestClicks) return clicks > bestClicks ? row : best;

    return numericRowMetric(row, "impressions") >
      numericRowMetric(best, "impressions")
      ? row
      : best;
  }, null);
};
